/**
 * Loaner Request Step - Iframe to Dell Demo Depot for hardware loan requests
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizardState } from '@/hooks/useWizardState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ExternalLink, 
  Package, 
  Users, 
  Cpu, 
  CheckCircle2,
  Info,
  Loader2,
  Home
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const DEMO_DEPOT_URL = 'https://www.delldemodepot.com';

export function LoanerRequestStep() {
  const { state } = useWizardState();
  const navigate = useNavigate();
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  const selectedModelsCount = state.modelSelections.selectedModelIds.length;
  const personasCount = state.personas.length;

  const handleComplete = () => {
    toast({
      title: 'Wizard Complete',
      description: 'Your configuration has been saved. You can now proceed to sizing analysis.',
    });
    navigate('/');
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const handleIframeError = () => {
    setIframeError(true);
    setIframeLoading(false);
  };

  const handleOpenInNewTab = () => {
    window.open(DEMO_DEPOT_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Request Demo Hardware
          </CardTitle>
          <CardDescription>
            Access Dell's loaner program to test AI infrastructure with your configured models and personas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Dell Demo Depot provides loaner hardware to qualified customers for testing and evaluation. 
              Submit your request below and a Dell representative will contact you.
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="gap-1">
              <Cpu className="h-3 w-3" />
              {selectedModelsCount} Model{selectedModelsCount !== 1 ? 's' : ''} Selected
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <Users className="h-3 w-3" />
              {personasCount} Persona{personasCount !== 1 ? 's' : ''} Configured
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Iframe Container */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Dell Demo Depot</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenInNewTab}
              className="gap-2"
            >
              Open in New Tab
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="relative">
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/95 z-10">
                <div className="text-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                  <p className="text-sm text-muted-foreground">Loading Dell Demo Depot...</p>
                </div>
              </div>
            )}

            {iframeError ? (
              <div className="min-h-[600px] flex items-center justify-center bg-muted/20 p-8">
                <div className="text-center space-y-4 max-w-md">
                  <div className="text-destructive">
                    <ExternalLink className="h-12 w-12 mx-auto mb-4" />
                  </div>
                  <h3 className="text-lg font-semibold">Unable to Load Demo Depot</h3>
                  <p className="text-sm text-muted-foreground">
                    The Dell Demo Depot page could not be loaded in this frame. 
                    Please click the button below to open it in a new tab.
                  </p>
                  <Button onClick={handleOpenInNewTab} className="gap-2">
                    Open Dell Demo Depot
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <iframe
                src={DEMO_DEPOT_URL}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                className="w-full min-h-[600px] md:min-h-[800px] border-0"
                title="Dell Demo Depot"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary and Completion */}
      <Card className="border-dashed bg-gradient-to-br from-accent/5 to-background">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Configuration Summary
          </CardTitle>
          <CardDescription>
            Your wizard configuration is complete and saved
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 text-sm">
            <div className="flex justify-between items-center p-3 rounded-lg bg-background/50 border border-border/50">
              <span className="text-muted-foreground">Total Users per Day</span>
              <span className="font-semibold">{state.environment.totalActiveUsersPerDay.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-background/50 border border-border/50">
              <span className="text-muted-foreground">Concurrent Users</span>
              <span className="font-semibold">{state.environment.concurrentUsers.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-background/50 border border-border/50">
              <span className="text-muted-foreground">Models Selected</span>
              <span className="font-semibold">{selectedModelsCount}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-background/50 border border-border/50">
              <span className="text-muted-foreground">Personas Configured</span>
              <span className="font-semibold">{personasCount}</span>
            </div>
          </div>

          <Button onClick={handleComplete} className="w-full gap-2" size="lg">
            <Home className="h-4 w-4" />
            Complete Wizard & Return Home
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
